import { getStaticShells } from "./indexManifest";
import { isDisabled, isHidden } from "./predicates";
import { withActionTracking } from "./trackObjectAction";
import type {
  ActionSurface,
  ObjectActionContext,
  ObjectActionHandlers,
  ObjectActionId,
  ObjectKindKey,
  ResolvedObjectAction,
} from "./types";

/**
 * Destructive actions always sort to the bottom of any menu/toolbar, with the
 * primary "delete" dead last — a near-universal convention and an explicit
 * product requirement. Everything else keeps its manifest declaration order
 * (Array.prototype.sort is stable), so this only moves the destructive tail.
 */
const DESTRUCTIVE_SORT_RANK: Partial<Record<ObjectActionId, number>> = {
  restore: 1,
  "delete-wrapper-only": 1,
  delete: 2,
  "remove-node": 2,
  "bulk-delete": 2,
};

function destructiveRank(id: ObjectActionId): number {
  return DESTRUCTIVE_SORT_RANK[id] ?? 0;
}

export function resolveObjectActions(
  kindKey: ObjectKindKey,
  surface: ActionSurface,
  context: ObjectActionContext,
  handlers: ObjectActionHandlers,
): ResolvedObjectAction[] {
  const shells = getStaticShells(kindKey, surface, context.kind);
  const resolved: ResolvedObjectAction[] = [];

  for (const shell of shells) {
    if (shell.presentation === "hidden") {
      continue;
    }
    if (isHidden(shell.hiddenWhen, context)) {
      continue;
    }

    const handler = handlers[shell.id];
    if (!handler) {
      continue;
    }

    resolved.push({
      id: shell.id,
      definition: shell.definition,
      presentation: shell.presentation,
      disabled: isDisabled(shell.disabledWhen, context),
      hidden: false,
      handler: withActionTracking(shell.id, context, handler, {
        presentation: shell.presentation,
      }),
    });
  }

  resolved.sort((a, b) => destructiveRank(a.id) - destructiveRank(b.id));

  return resolved;
}
